"use client";

import { findAllDefs } from "@noya-app/noya-schemas";
import type { LocalStorageSyncOptions } from "@noya-app/state-manager";
import {
  AccessType,
  createOrCastValue,
  isEmbeddedApp,
  localStorageSync,
  NoyaManager,
  parentFrameSync,
  PolicyAuthContext,
  setClientId,
  setClientImage,
  setClientName,
  Static,
  stubSync,
  SyncAdapter,
  TSchema,
  TypeGuard,
  webSocketSync,
} from "@noya-app/state-manager";
import { useEffect, useMemo, useState } from "react";
import { useBindNoyaManager, UseMultiplayerStateOptions } from "./hooks";
import { StateInspectorOptions } from "./inspector/StateInspector";

export type AppViewType = "editable" | "readOnly" | "preview";

export type AppTheme = "light" | "dark";

export type AppData<State> = {
  multiplayerUrl?: string;
  theme: AppTheme;
  viewType: AppViewType;
  initialState: State;
  inspector: boolean | StateInspectorOptions;
  userId?: string;
  isDemo: boolean;
  access?: AccessType;
  clientId?: string;
  clientName?: string;
  clientImage?: string;
  parentOrigin?: string;
  enableDocumentAI?: boolean;
};

export function createDefaultAppData<State>(
  initialState: State
): AppData<State> {
  return {
    theme: "light",
    viewType: "editable",
    initialState,
    inspector: false,
    isDemo: false,
    access: undefined,
  };
}

export type GetAppDataOptions = {
  window?: { location: { hash: string } };
  overrideExistingState?: boolean;
};

export function enforceSchema<State>(
  initialState: State,
  schema?: TSchema
): State {
  return schema
    ? (createOrCastValue({
        schema,
        value: initialState,
        defs: findAllDefs(schema),
      }) as State)
    : initialState;
}

export function parseAppDataParameter(
  options?: GetAppDataOptions
): AppData<unknown> | null {
  const window = options?.window ?? globalThis;
  const location = window.location;
  if (!location) return null;
  const urlHash = (location.hash || "").replace(/^#/, "");
  const params = new URLSearchParams(urlHash);
  const data = params.get("data");
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
}

function applyClientIdentityFromAppData(appData: {
  clientId?: string;
  clientName?: string;
  clientImage?: string;
}) {
  if (appData.clientId !== undefined) {
    setClientId(appData.clientId);
  }

  if (appData.clientName !== undefined) {
    setClientName(appData.clientName);
  }

  if (appData.clientImage !== undefined) {
    setClientImage(appData.clientImage);
  }
}

export function getAppData<State>(
  initialState: State | (() => State),
  schema?: TSchema,
  options?: GetAppDataOptions
): AppData<State> {
  const resolvedInitialState =
    initialState instanceof Function ? initialState() : initialState;

  let appData =
    parseAppDataParameter(options) ??
    createDefaultAppData(resolvedInitialState);

  if (options?.overrideExistingState || appData.initialState === null) {
    appData.initialState = resolvedInitialState;
  }

  appData.initialState = enforceSchema(appData.initialState, schema) as State;
  appData.isDemo = appData.isDemo ?? false;
  const fallbackAccess: AccessType =
    appData.viewType === "editable" ? "write" : "read";
  appData.access = appData.access ?? fallbackAccess;

  // If the parent frame provided a client identity (embedded mode), make sure
  // this frame adopts it so the clientId/name/image observables stay in sync.
  applyClientIdentityFromAppData(appData);

  return appData as AppData<State>;
}

export function getSyncAdapter<
  S,
  M extends object = object,
  E extends object = object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
>(params?: {
  multiplayerUrl?: string;
  offlineStorageKey?: string;
  debug?: boolean;
  policyAuthContext?: PolicyAuthContext;
  serverScripts?: LocalStorageSyncOptions["serverScripts"];
  git?: LocalStorageSyncOptions["git"];
  parentOrigin?: string;
  enableDocumentAI?: boolean;
}): SyncAdapter<S, M, E, MenuT, I> {
  const {
    multiplayerUrl,
    offlineStorageKey,
    debug,
    policyAuthContext,
    serverScripts,
    git,
    parentOrigin,
    enableDocumentAI,
  } = params ?? {};

  return isEmbeddedApp()
    ? parentFrameSync<S, M, E, MenuT, I>({
        debug,
        policyAuthContext,
        origin: parentOrigin,
        enableDocumentAI,
      })
    : multiplayerUrl
      ? webSocketSync<S, M, E, MenuT, I>({ url: multiplayerUrl, debug })
      : offlineStorageKey
        ? localStorageSync<S, M, E, MenuT, I>({
            key: offlineStorageKey,
            ...(serverScripts ? { serverScripts } : {}),
            ...(git ? { git } : {}),
          })
        : stubSync<S, M, E, MenuT, I>();
}

export type UseNoyaStateOptions<
  S,
  M extends object = object,
  E extends object = object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
> = UseMultiplayerStateOptions<S, M, E, MenuT, I> & {
  offlineStorageKey?: string;
  serverScripts?: LocalStorageSyncOptions["serverScripts"];
  git?: LocalStorageSyncOptions["git"];
};

export type UseNoyaStateResult<
  S,
  M extends object = object,
  E extends object = object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
> = [
  ReturnType<typeof useBindNoyaManager<S, M, E, MenuT, I>>[0],
  ReturnType<typeof useBindNoyaManager<S, M, E, MenuT, I>>[1],
  ReturnType<typeof useBindNoyaManager<S, M, E, MenuT, I>>[2] & {
    theme: AppTheme;
    viewType: AppViewType;
    isDemo: boolean;
  },
];

/**
 * Sync data locally or to a multiplayer server.
 *
 * There are 3 ways to use this hook:
 *
 * 1. Pass an initial state: `useNoyaState(initialState)`
 * 2. Pass an initial state and a schema: `useNoyaState(initialState, { schema })`
 * 3. Pass only a schema, and a conforming initial state is created automatically: `useNoyaState({ schema })`
 */
export function useNoyaStateInternal<
  S,
  M extends object = object,
  E extends object = object,
  MenuT extends string = string,
  I extends Record<string, any> = Record<string, unknown>,
  O extends UseNoyaStateOptions<S, M, E, MenuT, I> = UseNoyaStateOptions<
    S,
    M,
    E,
    MenuT,
    I
  >,
>(
  ...args:
    | [
        initialState: O["schema"] extends TSchema
          ? Static<O["schema"]> | (() => Static<O["schema"]>)
          : S | (() => S),
        options?: O,
      ]
    | [options: O & { schema: TSchema }]
): UseNoyaStateResult<
  O["schema"] extends TSchema ? Static<O["schema"]> : S,
  M,
  E,
  MenuT,
  I
> {
  const [initialState, options] =
    typeof args[0] === "object" &&
    args[0] !== null &&
    "schema" in args[0] &&
    TypeGuard.IsSchema(args[0].schema)
      ? [null, args[0]]
      : [args[0], args[1]];

  // Keep these stable after initial render
  const [
    {
      multiplayerUrl,
      inspector,
      initialState: noyaAppInitialState,
      theme,
      viewType,
      userId,
      isDemo,
      access,
      parentOrigin,
      enableDocumentAI,
    },
  ] = useState(() => {
    return getAppData(initialState, options?.schema, {
      overrideExistingState: options?.overrideExistingState,
    });
  });

  const basePolicyAuthContext = useMemo((): PolicyAuthContext | undefined => {
    if (!access && userId === undefined) return undefined;

    const context: PolicyAuthContext = {};

    if (access) {
      context.access = access;
    }

    if (userId !== undefined) {
      context.id = userId ?? null;
    }

    return context;
  }, [access, userId]);

  const sync = useMemo((): SyncAdapter<S, M, E, MenuT, I> => {
    return getSyncAdapter<S, M, E, MenuT, I>({
      multiplayerUrl,
      offlineStorageKey: options?.offlineStorageKey,
      debug: options?.debug,
      policyAuthContext: basePolicyAuthContext,
      serverScripts: options?.serverScripts,
      git: options?.git,
      parentOrigin,
      enableDocumentAI,
    });
  }, [
    multiplayerUrl,
    options?.offlineStorageKey,
    options?.debug,
    basePolicyAuthContext,
    options?.serverScripts,
    options?.git,
    parentOrigin,
    enableDocumentAI,
  ]);

  const [noyaManager] = useState(
    () =>
      new NoyaManager<S, M, E, MenuT, I>(noyaAppInitialState as S, {
        ...options,
        resourceUrlBase:
          options?.resourceUrlBase ??
          parentOrigin ??
          (typeof globalThis.location === "undefined"
            ? undefined
            : globalThis.location.origin === "null"
              ? undefined
              : globalThis.location.origin),
      })
  );

  useEffect(() => {
    if (!userId) return;

    noyaManager.sharedConnectionDataManager.setCurrentConnectionId(userId);
    noyaManager.userManager.setCurrentConnectionId(userId);
    noyaManager.userManager.setCurrentUserId(userId);
  }, [noyaManager, userId]);

  useEffect(() => {
    if (!basePolicyAuthContext) return;

    noyaManager.multiplayerStateManager.setPolicyAuthContext(
      basePolicyAuthContext
    );
  }, [noyaManager, basePolicyAuthContext]);

  const result = useBindNoyaManager<S, M, E, MenuT, I>(noyaManager, {
    inspector: options?.inspector ?? inspector,
    sync: options?.sync ?? sync,
  });

  const [, , extras] = result;

  const mergedExtras = useMemo(() => {
    return { ...extras, theme, viewType, isDemo } as const;
  }, [extras, theme, viewType, isDemo]);

  return [result[0], result[1], mergedExtras] as UseNoyaStateResult<
    O["schema"] extends TSchema ? Static<O["schema"]> : S,
    M,
    E,
    MenuT,
    I
  >;
}

// function typeTest() {
//   const [a, setA] = useNoyaState(7);
//   const [b, setB] = useNoyaState(7, {
//     schema: Type.Number(),
//   });
//   const [c, setC] = useNoyaState({
//     schema: Type.Number(),
//   });
// }
